import type { JSX } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@artosera/ui';
import {
  useTousContratsArtistes,
  type ContratArtistePresente,
} from '../../api/contratsArtistes.js';
import { useArtiste } from '../../api/artistes.js';
import { useShellContext } from '../shell/context.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { dateContrat, erreurContrat } from '../contrats/contratUi.js';
import styles from './EcheancesContratsPage.module.css';
export function EcheancesContratsPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const contrats = useTousContratsArtistes(galerieSlug, { etat: 'en_vigueur' });
  const groupes = ['echus', 'bientot', 'plusTard', 'sansEcheance'] as const;
  const groupe = (c: ContratArtistePresente) =>
    c.finLe === null
      ? 'sansEcheance'
      : c.echu
        ? 'echus'
        : (c.joursAvantEcheance ?? Infinity) <= 90
          ? 'bientot'
          : 'plusTard';
  const liste = [...(contrats.data ?? [])].sort((a, b) =>
    (a.finLe ?? '9999').localeCompare(b.finLe ?? '9999'),
  );
  return (
    <section className={styles.page} data-ui="page">
      <header data-ui="page-heading">
        <h1 className={styles.titre}>{t('regie:contratsArtistes.echeances.titre')}</h1>
      </header>
      {contrats.isPending && <p role="status">{t('commun:chargement')}</p>}
      {contrats.isError && (
        <p role="alert">{erreurContrat(contrats.error, t('commun:erreurReseau'))}</p>
      )}
      {contrats.data?.length === 0 && <p>{t('regie:contratsArtistes.aucun')}</p>}
      {groupes
        .filter((g) => liste.some((c) => groupe(c) === g))
        .map((g) => (
          <section key={g} className={styles.groupe}>
            <h2 className={styles.groupeTitre}>
              {t(`regie:contratsArtistes.echeances.${g}`)}
              <span className={styles.groupeCompte} aria-hidden="true">
                {liste.filter((c) => groupe(c) === g).length}
              </span>
            </h2>
            <div className={styles.liste} data-ui="object-list">
              {liste
                .filter((c) => groupe(c) === g)
                .map((c) => (
                  <Ligne key={c.id} contrat={c} galerieSlug={galerieSlug} />
                ))}
            </div>
          </section>
        ))}
    </section>
  );
}
function Ligne({
  contrat: c,
  galerieSlug,
}: {
  contrat: ContratArtistePresente;
  galerieSlug: string;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const artiste = useArtiste(galerieSlug, c.artisteId);
  return (
    <Link
      className={styles.ligne}
      data-ui="object-row"
      to={`/${galerieSlug}/artistes/${c.artisteId}`}
    >
      <Monogramme nom={artiste.data?.nom} taille={40} />
      <strong className={styles.nom}>{artiste.data?.nom ?? '—'}</strong>
      <span className={styles.date}>
        {c.finLe ? dateContrat(c.finLe, i18n.language) : t('regie:contratsArtistes.sansEcheance')}
      </span>
      {c.echu && <Badge tone="neutral">{t('regie:contratsArtistes.echeances.echus')}</Badge>}
    </Link>
  );
}
